import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, utf8ToBytes } from '@noble/hashes/utils.js';
import { getStorageItem, setStorageItem } from 'utils/ui/storage';

/**
 * sessionStorage key of the per-tab VOIP id.
 *
 * AVS-4454: rotated from `voip-tab-id`. Before the fix `voipMasters` published the raw id to
 * every participant; ignoring the legacy key mints a fresh id once, so a raw id leaked by a
 * pre-fix slot never matches a live signaling row again.
 */
export const VOIP_TAB_ID_STORAGE_KEY = 'voip-tab-id-v2';

/**
 * Stable per-tab identifier for VOIP master election (AVS-4302).
 *
 * Backed by sessionStorage (NOT the in-memory `localAveTabId`) so it survives a
 * page reload but stays unique per browser tab. A reload must keep the same tab as
 * master, while a second tab of the same dealer must resolve to a distinct id (and
 * therefore a standby).
 *
 * SECRET to this tab: it is the signaling `clientId`, the selector `supersedeClient` retires
 * rows by. Never publish it — `voipMasters` carries {@link getVoipSlotId} instead (AVS-4454).
 *
 * Mirrors `bandwidthManager.getTabId()`.
 */
export const getVoipTabId = (): string => {
  const existing = getStorageItem('sessionStorage', VOIP_TAB_ID_STORAGE_KEY);
  if (existing) return existing;

  const id = `voip-${crypto.randomUUID()}`;
  setStorageItem('sessionStorage', VOIP_TAB_ID_STORAGE_KEY, id);
  return id;
};

let slotMemo: { sessionId: string | undefined; tabId: string; slotId: string } | undefined;

/**
 * AVS-4454 — this tab's id as `voipMasters` publishes it: hex SHA-256 of
 * `${sessionId}:${getVoipTabId()}`, the same digest the signaling server sends as `clientIdHash`
 * (`hashClientId`). Synchronous on purpose: `amIMaster` / `isVoipStandby` are sync getters.
 * Every claim site must call this once a session id is known — the `undefined` case hashes
 * `:${tabId}` (no session-id prefix), which is a valid digest but matches no real slot.
 */
export const getVoipSlotId = (sessionId: string | undefined): string => {
  const tabId = getVoipTabId();
  if (!slotMemo || slotMemo.sessionId !== sessionId || slotMemo.tabId !== tabId) {
    slotMemo = { sessionId, tabId, slotId: bytesToHex(sha256(utf8ToBytes(`${sessionId ?? ''}:${tabId}`))) };
  }
  return slotMemo.slotId;
};
